/**
 * High-level E2E session manager. Always on — no disable switch.
 */

import { e2eAPI } from '../../api';
import { b64ToBytes, bytesToB64, textDecoder, textEncoder } from './bytes';
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  deserializeKeyPair,
  decodeWireBody,
  E2E_VERSION,
  fingerprintHex,
  formatSafetyNumber,
  generateEd25519Pair,
  generateX25519Pair,
  isE2EWireBody,
  kdf,
  nextKeyId,
  OTP_BATCH_SIZE,
  serializeKeyPair,
  signPrekey,
  verifySignedPrekey,
  x3dhInitiate,
  x3dhRespond,
} from './cryptoCore';
import {
  deserializeState,
  initAlice,
  initBob,
  ratchetDecrypt,
  ratchetEncrypt,
  replayOwnSend,
  serializeState,
} from './doubleRatchet';
import {
  clearAllE2E,
  countOneTimePrekeys,
  deleteSession,
  getMeta,
  getSession,
  notifyE2EChanged,
  peekOneTimePrekey,
  putOneTimePrekeys,
  putSession,
  removeOneTimePrekey,
  setMeta,
  clearAllSessions,
} from './keystore';
import { e2eLog } from './e2eLog';

const META_IDENTITY = 'identity';
const META_SIGNING = 'signing';
const META_SIGNED_PREKEY = 'signedPrekey';
const META_DEVICE = 'device';
const META_SYNCED_AT = 'serverSyncedAt';
const META_SYNC_ATTEMPT_AT = 'serverSyncAttemptAt';
const META_SESSION_EPOCH = 'sessionEpoch';
const SESSION_EPOCH = 7; // bump: fix cannotDecrypt recursion; wipe poisoned DR sessions
const LOCAL_PREFIX = 'local1.';
const SERVER_SYNC_TTL_MS = 12 * 60 * 60 * 1000;
const SYNC_RETRY_COOLDOWN_MS = 60 * 1000;
const OTP_REFILL_AT = 15;
const OTP_REFILL_TARGET = OTP_BATCH_SIZE;

let ensurePromise = null;

/** Serialize DR ops per chat so history/WS decrypt cannot race the same session. */
const chatLocks = new Map();
const withChatLock = async (chatId, fn) => {
  const key = String(chatId || '');
  const prev = chatLocks.get(key) || Promise.resolve();
  let release;
  const gate = new Promise((resolve) => { release = resolve; });
  const next = prev.then(() => gate);
  chatLocks.set(key, next);
  await prev.catch(() => {});
  try {
    return await fn();
  } finally {
    release();
    if (chatLocks.get(key) === next) {
      chatLocks.delete(key);
    }
  }
};

/** In-memory plaintext caches (sealed when written to IDB). */
const outboundPlainByBody = new Map();
const outboundPlainByHeader = new Map();
const plainByMsgId = new Map();
/** Own voice plaintext: headerKey / msgId → { mime, audioB64 } */
const outboundVoiceByHeader = new Map();
const outboundVoiceByMsgId = new Map();
let outboundLoaded = false;
let persistOutboundChain = Promise.resolve();
let persistPlainChain = Promise.resolve();
let persistVoiceChain = Promise.resolve();
const CACHE_SEAL_INFO = 'LynxLocalSeal';
const META_OUTBOUND_PLAIN = 'outboundPlain';
const META_OUTBOUND_HEADER = 'outboundPlainByHeader';
const META_PLAIN_BY_MSG = 'plainByMsgId';
const META_OUTBOUND_VOICE = 'outboundVoice';
const MAX_VOICE_CACHE = 40;
const MAX_VOICE_B64_CHARS = 900_000; // ~675KB binary cap per entry

const headerCacheKey = (header) => {
  if (!header || typeof header !== 'object') return null;
  const dh = typeof header.dh === 'string' ? header.dh : '';
  if (!dh && header.type !== 'local') return null;
  return `${header.type || 'msg'}|${dh}|${header.n ?? ''}|${header.pn ?? ''}`;
};

const parseHeader = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

/** Resolve sender-side plaintext without ratcheting. */
export const resolveOwnPlaintext = (message) => {
  if (!message || typeof message !== 'object') return null;
  const msgId = message.id != null ? String(message.id).toLowerCase() : null;
  if (msgId && plainByMsgId.has(msgId)) {
    return plainByMsgId.get(msgId);
  }
  const body = typeof message.message === 'string' ? message.message : null;
  if (body && outboundPlainByBody.has(body)) {
    return outboundPlainByBody.get(body);
  }
  const hk = headerCacheKey(parseHeader(message.e2e_header));
  if (hk && outboundPlainByHeader.has(hk)) {
    return outboundPlainByHeader.get(hk);
  }
  return null;
};

const getLocalSealKey = async () => {
  const identity = await loadIdentity();
  if (!identity?.secretKey) return null;
  return kdf(identity.secretKey, CACHE_SEAL_INFO, 32);
};

const sealCacheObject = async (obj) => {
  const key = await getLocalSealKey();
  if (!key) return null;
  const packed = await aesGcmEncrypt(key, textEncoder.encode(JSON.stringify(obj || {})));
  return { v: 1, alg: 'local-seal', blob: bytesToB64(packed) };
};

const unsealCacheObject = async (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.v === 1 && typeof raw.blob === 'string') {
    const key = await getLocalSealKey();
    if (!key) return null;
    const plain = await aesGcmDecrypt(key, b64ToBytes(raw.blob));
    const parsed = JSON.parse(textDecoder.decode(plain));
    return parsed && typeof parsed === 'object' ? parsed : null;
  }
  // Legacy / plain map
  return raw;
};

/** Prefer plain IDB maps — sealing was dropping plaintext after refresh. */
const readCacheObject = async (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.v === 1 && typeof raw.blob === 'string') {
    try {
      return await unsealCacheObject(raw);
    } catch (_) {
      return null;
    }
  }
  return raw;
};

const fillStringMap = (map, obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
  Object.entries(obj).forEach(([k, v]) => {
    if (typeof k === 'string' && typeof v === 'string') {
      map.set(k, v);
    }
  });
};

const trimMap = (map, max) => {
  while (map.size > max) {
    const first = map.keys().next().value;
    map.delete(first);
  }
};

/** Re-read plaintext caches from IDB and merge into memory. */
const mergeOutboundCacheFromDisk = async () => {
  outboundLoaded = true;
  try {
    const raw = await getMeta(META_OUTBOUND_PLAIN);
    fillStringMap(outboundPlainByBody, await readCacheObject(raw));
  } catch (_) {
    // ignore
  }
  try {
    const rawHdr = await getMeta(META_OUTBOUND_HEADER);
    fillStringMap(outboundPlainByHeader, await readCacheObject(rawHdr));
  } catch (_) {
    // ignore
  }
  try {
    const rawIds = await getMeta(META_PLAIN_BY_MSG);
    fillStringMap(plainByMsgId, await readCacheObject(rawIds));
  } catch (_) {
    // ignore
  }
  try {
    const rawVoice = await getMeta(META_OUTBOUND_VOICE);
    const obj = await readCacheObject(rawVoice);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      Object.entries(obj).forEach(([k, v]) => {
        if (!v || typeof v !== 'object' || typeof v.audioB64 !== 'string') return;
        if (k.startsWith('id:')) {
          outboundVoiceByMsgId.set(k.slice(3), v);
        } else {
          outboundVoiceByHeader.set(k, v);
        }
      });
    }
  } catch (_) {
    // ignore
  }
};

const loadOutboundCache = async () => {
  if (outboundLoaded) return;
  await mergeOutboundCacheFromDisk();
};

const persistOutboundCache = async () => {
  persistOutboundChain = persistOutboundChain.then(async () => {
    trimMap(outboundPlainByBody, 150);
    trimMap(outboundPlainByHeader, 150);
    const bodyObj = {};
    outboundPlainByBody.forEach((v, k) => { bodyObj[k] = v; });
    const hdrObj = {};
    outboundPlainByHeader.forEach((v, k) => { hdrObj[k] = v; });
    // Device-local plaintext maps (IDB already holds private keys).
    await setMeta(META_OUTBOUND_PLAIN, bodyObj);
    await setMeta(META_OUTBOUND_HEADER, hdrObj);
    e2eLog('cache.persist_outbound', {
      extra: { bodies: outboundPlainByBody.size, headers: outboundPlainByHeader.size },
    });
  }).catch((err) => {
    e2eLog('cache.persist_outbound_fail', { err: err?.message || String(err) });
  });
  return persistOutboundChain;
};

const persistPlainByMsgId = async () => {
  persistPlainChain = persistPlainChain.then(async () => {
    trimMap(plainByMsgId, 400);
    const obj = {};
    plainByMsgId.forEach((v, k) => { obj[k] = v; });
    await setMeta(META_PLAIN_BY_MSG, obj);
    e2eLog('cache.persist_plain', { extra: { ids: plainByMsgId.size } });
  }).catch((err) => {
    e2eLog('cache.persist_plain_fail', { err: err?.message || String(err) });
  });
  return persistPlainChain;
};

const persistOutboundVoiceCache = async () => {
  persistVoiceChain = persistVoiceChain.then(async () => {
    trimMap(outboundVoiceByHeader, MAX_VOICE_CACHE);
    trimMap(outboundVoiceByMsgId, MAX_VOICE_CACHE);
    const obj = {};
    outboundVoiceByHeader.forEach((v, k) => { obj[k] = v; });
    outboundVoiceByMsgId.forEach((v, k) => { obj[`id:${k}`] = v; });
    await setMeta(META_OUTBOUND_VOICE, obj);
    e2eLog('cache.persist_voice', {
      extra: { headers: outboundVoiceByHeader.size, ids: outboundVoiceByMsgId.size },
    });
  }).catch((err) => {
    e2eLog('cache.persist_voice_fail', { err: err?.message || String(err) });
  });
  return persistVoiceChain;
};

const rememberOutboundVoice = (header, mime, plainBytes, msgId = null) => {
  if (!plainBytes || !(plainBytes instanceof Uint8Array) || plainBytes.length === 0) {
    return;
  }
  const audioB64 = bytesToB64(plainBytes);
  if (audioB64.length > MAX_VOICE_B64_CHARS) {
    return;
  }
  const entry = {
    mime: (mime || 'audio/webm').slice(0, 64),
    audioB64,
  };
  const hk = headerCacheKey(parseHeader(header));
  if (hk) {
    outboundVoiceByHeader.set(hk, entry);
    trimMap(outboundVoiceByHeader, MAX_VOICE_CACHE);
  }
  if (msgId != null) {
    const id = String(msgId).toLowerCase();
    if (id) {
      outboundVoiceByMsgId.set(id, entry);
      trimMap(outboundVoiceByMsgId, MAX_VOICE_CACHE);
    }
  }
  persistOutboundVoiceCache().catch(() => {});
};

export const bindOutboundVoiceMsgId = (msgId, e2eHeader) => {
  const id = msgId != null ? String(msgId).toLowerCase() : '';
  if (!id) return;
  const hk = headerCacheKey(parseHeader(e2eHeader));
  if (hk && outboundVoiceByHeader.has(hk)) {
    outboundVoiceByMsgId.set(id, outboundVoiceByHeader.get(hk));
    trimMap(outboundVoiceByMsgId, MAX_VOICE_CACHE);
    persistOutboundVoiceCache().catch(() => {});
  }
};

const lookupOutboundVoice = (header, msgId = null) => {
  if (msgId != null) {
    const id = String(msgId).toLowerCase();
    if (id && outboundVoiceByMsgId.has(id)) {
      return outboundVoiceByMsgId.get(id);
    }
  }
  const hk = headerCacheKey(parseHeader(header));
  if (hk && outboundVoiceByHeader.has(hk)) {
    return outboundVoiceByHeader.get(hk);
  }
  return null;
};

const rememberPlain = (msgId, plaintext) => {
  if (msgId == null || plaintext == null) return;
  const id = String(msgId).toLowerCase();
  if (!id) return;
  plainByMsgId.set(id, String(plaintext));
  trimMap(plainByMsgId, 500);
  persistPlainByMsgId().catch(() => {});
};

const rememberOutbound = (body, plaintext, msgId = null, header = null) => {
  if (plaintext == null) return;
  const plain = String(plaintext);
  if (body) {
    outboundPlainByBody.set(String(body), plain);
    trimMap(outboundPlainByBody, 200);
  }
  const hk = headerCacheKey(parseHeader(header));
  if (hk) {
    outboundPlainByHeader.set(hk, plain);
    trimMap(outboundPlainByHeader, 200);
  }
  persistOutboundCache().catch(() => {});
  if (msgId != null) {
    rememberPlain(msgId, plain);
  }
};

const makeDeviceId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const guessDeviceMeta = () => {
  if (typeof navigator === 'undefined') {
    return { label: 'device', platform: 'unknown', client: 'Lynx Web' };
  }
  const ua = navigator.userAgent || '';
  let browser = 'browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  let os = 'Unknown';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad/i.test(ua)) os = 'iOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  const mobile = /Android|iPhone|iPad|Mobile/i.test(ua);
  return {
    label: `${browser} on ${os}`,
    platform: mobile ? 'mobile' : 'desktop',
    client: `Lynx Web · ${browser}`,
  };
};

const loadIdentity = async () => {
  const row = await getMeta(META_IDENTITY);
  return row ? deserializeKeyPair(row) : null;
};

const loadSigning = async () => {
  const row = await getMeta(META_SIGNING);
  return row ? deserializeKeyPair(row) : null;
};

const loadSignedPrekey = async () => {
  const row = await getMeta(META_SIGNED_PREKEY);
  if (!row) return null;
  return {
    keyId: row.keyId,
    signature: row.signature || null,
    ...deserializeKeyPair(row),
  };
};

export const hasE2EIdentity = async () => Boolean(await loadIdentity());

export const getLocalDeviceId = async () => {
  const row = await getMeta(META_DEVICE);
  return row?.deviceId || null;
};

export const getLocalIdentityPublicB64 = async () => {
  const id = await loadIdentity();
  return id ? bytesToB64(id.publicKey) : null;
};

export const getSafetyNumber = async (peerIdentityB64) => {
  const local = await loadIdentity();
  if (!local || !peerIdentityB64) return null;
  const peer = b64ToBytes(peerIdentityB64);
  const a = bytesToB64(local.publicKey);
  const [first, second] = a < peerIdentityB64
    ? [local.publicKey, peer]
    : [peer, local.publicKey];
  return formatSafetyNumber(fingerprintHex(first, second));
};

const buildOtpBatch = (startId, count = OTP_BATCH_SIZE) => {
  const rows = [];
  const upload = [];
  for (let i = 0; i < count; i += 1) {
    const keyId = startId + i;
    const pair = generateX25519Pair();
    rows.push({
      keyId,
      secretKey: bytesToB64(pair.secretKey),
      publicKey: bytesToB64(pair.publicKey),
    });
    upload.push({
      key_id: keyId,
      public_key: bytesToB64(pair.publicKey),
    });
  }
  return { rows, upload, nextId: startId + count };
};

/** Ensure identity/device keys exist locally (no network). */
export const ensureLocalE2E = async () => {
  const epoch = Number(await getMeta(META_SESSION_EPOCH)) || 0;
  if (epoch < SESSION_EPOCH) {
    await clearAllSessions();
    await setMeta(META_SESSION_EPOCH, SESSION_EPOCH);
  }
  let created = false;
  let identity = await loadIdentity();
  let signing = await loadSigning();
  let device = await getMeta(META_DEVICE);

  if (!identity) {
    identity = generateX25519Pair();
    await setMeta(META_IDENTITY, serializeKeyPair(identity));
    created = true;
  }
  if (!signing) {
    signing = generateEd25519Pair();
    await setMeta(META_SIGNING, serializeKeyPair(signing));
    created = true;
  }
  if (!device?.deviceId) {
    device = { deviceId: makeDeviceId() };
    await setMeta(META_DEVICE, device);
    created = true;
  }

  let signed = await loadSignedPrekey();
  if (!signed) {
    const pair = generateX25519Pair();
    const keyId = 1;
    const signature = signPrekey(pair.publicKey, signing.secretKey);
    signed = { keyId, ...pair, signature };
    await setMeta(META_SIGNED_PREKEY, {
      keyId,
      ...serializeKeyPair(pair),
      signature: bytesToB64(signature),
    });
    created = true;
  }
  // Load sealed plaintext caches only after identity exists (seal key derives from it).
  await loadOutboundCache();
  return { identity, signing, device, signed, created };
};

/** Bootstrap identity + publish prekeys. Skips network unless needed / cooled down. */
export const ensureE2E = async ({ forceSync = false } = {}) => {
  if (ensurePromise) return ensurePromise;
  ensurePromise = (async () => {
    const local = await ensureLocalE2E();
    const { identity, signing, device, signed, created } = local;

    const otpCount = await countOneTimePrekeys();
    let otpUpload = [];
    if (otpCount < OTP_REFILL_AT) {
      const start = Number(await getMeta('otpNextId')) || 1;
      const batch = buildOtpBatch(start, OTP_REFILL_TARGET);
      await putOneTimePrekeys(batch.rows);
      otpUpload = batch.upload;
      await setMeta('otpNextId', batch.nextId);
    }

    const syncedAt = Number(await getMeta(META_SYNCED_AT)) || 0;
    const attemptAt = Number(await getMeta(META_SYNC_ATTEMPT_AT)) || 0;
    const stale = !syncedAt || (Date.now() - syncedAt) > SERVER_SYNC_TTL_MS;
    const inCooldown = !forceSync && attemptAt && (Date.now() - attemptAt) < SYNC_RETRY_COOLDOWN_MS;
    const needsSync = (forceSync || created || stale || otpUpload.length > 0) && !inCooldown;

    if (needsSync) {
      await setMeta(META_SYNC_ATTEMPT_AT, Date.now());
      try {
        const spkSig = signed.signature
          ? (typeof signed.signature === 'string' ? signed.signature : bytesToB64(signed.signature))
          : bytesToB64(signPrekey(signed.publicKey, signing.secretKey));

        await e2eAPI.registerDevice({
          device_id: device.deviceId,
          ...(() => {
            const meta = guessDeviceMeta();
            return {
              label: meta.label,
              platform: meta.platform,
              client: meta.client,
            };
          })(),
          identity_key: bytesToB64(identity.publicKey),
          signing_key: bytesToB64(signing.publicKey),
          signed_prekey: {
            key_id: signed.keyId,
            public_key: bytesToB64(signed.publicKey),
            signature: spkSig,
          },
          one_time_prekeys: otpUpload,
        }, { timeout: 30000 });
        await setMeta(META_SYNCED_AT, Date.now());
      } catch (err) {
        // Local keys are enough for favorites seal + later retry.
        // Throwing here blocked Chrome sends when /e2e/devices hit 401.
        e2eLog('ensure.sync_fail', {
          err: err?.message || String(err),
          extra: { created: Boolean(created), forceSync: Boolean(forceSync) },
        });
        if (forceSync) {
          throw err;
        }
      }
    }

    if (created) {
      notifyE2EChanged();
    }
    return { deviceId: device.deviceId };
  })().finally(() => {
    ensurePromise = null;
  });
  return ensurePromise;
};

/** @deprecated use ensureE2E */
export const enableE2E = ensureE2E;

export const isE2EEnabled = async () => {
  await ensureE2E();
  return true;
};

export const resetE2ECompletely = async () => {
  await clearAllE2E();
  notifyE2EChanged();
  return ensureE2E({ forceSync: true });
};

export const replenishPrekeysIfNeeded = async () => {
  const available = await countOneTimePrekeys();
  if (available >= OTP_REFILL_AT) return;
  await ensureE2E();
  const device = await getMeta(META_DEVICE);
  const signing = await loadSigning();
  const signed = await loadSignedPrekey();
  if (!device?.deviceId || !signing || !signed) return;

  // Re-check after ensure — it may have already refilled.
  if ((await countOneTimePrekeys()) >= OTP_REFILL_AT) return;

  const nextId = Number(await getMeta('otpNextId')) || 1000;
  const batch = buildOtpBatch(nextId, OTP_REFILL_TARGET);
  await putOneTimePrekeys(batch.rows);
  await setMeta('otpNextId', batch.nextId);

  const rotateSpk = available < 5;
  let signedPayload = null;
  if (rotateSpk) {
    const pair = generateX25519Pair();
    const keyId = nextKeyId(signed.keyId);
    const signature = signPrekey(pair.publicKey, signing.secretKey);
    await setMeta(META_SIGNED_PREKEY, {
      keyId,
      ...serializeKeyPair(pair),
      signature: bytesToB64(signature),
    });
    signedPayload = {
      key_id: keyId,
      public_key: bytesToB64(pair.publicKey),
      signature: bytesToB64(signature),
    };
  }

  await e2eAPI.replenishPrekeys(device.deviceId, {
    signed_prekey: signedPayload,
    one_time_prekeys: batch.upload,
  }, { timeout: 30000 });
  await setMeta(META_SYNCED_AT, Date.now());
};

const saveDrState = async (chatId, state) => {
  await putSession(chatId, serializeState(state));
};

const loadDrState = async (chatId) => {
  const row = await getSession(chatId);
  return row?.state ? deserializeState(row.state) : null;
};

const ensureAliceSession = async (chatId, peerConnectId) => {
  let state = await loadDrState(chatId);
  // Never mint a new X3DH if a session already exists — overwriting DHs
  // desyncs the peer and shows up as permanent cannot-decrypt after refresh.
  if (state?.established) {
    if (!state.CKs) {
      throw new Error('session not ready to send yet');
    }
    e2eLog('alice.reuse', { chatId, state });
    return state;
  }

  await ensureE2E();
  const identity = await loadIdentity();
  if (!identity) {
    throw new Error('e2e not initialized');
  }

  const { data: bundle } = await e2eAPI.getBundle(peerConnectId, { consume_otp: false });
  if (!bundle?.identity_key || !bundle?.signed_prekey) {
    throw new Error('peer has no e2e device');
  }

  // Re-check after network — another send/decrypt may have created the session.
  state = await loadDrState(chatId);
  if (state?.established) {
    if (!state.CKs) {
      throw new Error('session not ready to send yet');
    }
    e2eLog('alice.reuse_after_bundle', { chatId, state });
    return state;
  }

  const peerIk = b64ToBytes(bundle.identity_key);
  const peerSpk = b64ToBytes(bundle.signed_prekey.public_key);
  const peerSig = b64ToBytes(bundle.signed_prekey.signature);
  const peerSigning = b64ToBytes(bundle.signing_key);
  if (!verifySignedPrekey(peerSpk, peerSig, peerSigning)) {
    throw new Error('invalid signed prekey signature');
  }

  const ephemeral = generateX25519Pair();
  // Skip one-time prekeys for now — OPK private-key sync across
  // reinstalls/refills was causing permanent "prekey missing" failures.
  const shared = x3dhInitiate({
    identitySecret: identity.secretKey,
    ephemeralSecret: ephemeral.secretKey,
    peerIdentityPublic: peerIk,
    peerSignedPrekeyPublic: peerSpk,
    peerOneTimePrekeyPublic: null,
  });

  state = initAlice(
    shared,
    peerSpk,
    peerIk,
    bundle.device_id,
    {
      spkId: bundle.signed_prekey.key_id,
      opkId: null,
      ephemeralPublic: ephemeral.publicKey,
      identityPublic: identity.publicKey,
    },
  );
  await saveDrState(chatId, state);
  e2eLog('alice.new', {
    chatId,
    state,
    extra: { peerDevice: String(bundle.device_id || '').slice(0, 8) },
  });
  try {
    await e2eAPI.markChatOpen(chatId);
  } catch (_) {
    // non-fatal
  }
  return state;
};

const acceptPrekeyMessage = async (chatId, header) => {
  await ensureLocalE2E();
  const identity = await loadIdentity();
  const signed = await loadSignedPrekey();
  if (!identity || !signed) {
    throw new Error('e2e not initialized');
  }

  let otpSecret = null;
  let otpKeyId = null;
  if (header.opk_id != null) {
    const otp = await peekOneTimePrekey(header.opk_id);
    if (!otp) {
      throw new Error(`one-time prekey ${header.opk_id} missing on this device`);
    }
    otpSecret = b64ToBytes(otp.secretKey);
    otpKeyId = header.opk_id;
  }

  const peerIk = b64ToBytes(header.ik);
  const peerEk = b64ToBytes(header.ek);
  const shared = x3dhRespond({
    identitySecret: identity.secretKey,
    signedPrekeySecret: signed.secretKey,
    oneTimePrekeySecret: otpSecret,
    peerIdentityPublic: peerIk,
    peerEphemeralPublic: peerEk,
  });

  const state = initBob(shared, {
    secretKey: signed.secretKey,
    publicKey: signed.publicKey,
  }, peerIk, null);
  state._pendingOtpKeyId = otpKeyId;
  return state;
};

const encryptLocalText = async (plaintext) => {
  // Favorites must not depend on server device sync.
  await ensureLocalE2E();
  const identity = await loadIdentity();
  if (!identity?.secretKey) {
    throw new Error('e2e identity missing');
  }
  const key = kdf(identity.secretKey, 'LynxLocalSeal', 32);
  const packed = await aesGcmEncrypt(key, textEncoder.encode(plaintext));
  return `${LOCAL_PREFIX}${bytesToB64(packed)}`;
};

const decryptLocalText = async (body) => {
  await ensureLocalE2E();
  const identity = await loadIdentity();
  if (!identity?.secretKey) {
    throw new Error('e2e identity missing');
  }
  const key = kdf(identity.secretKey, 'LynxLocalSeal', 32);
  const packed = b64ToBytes(body.slice(LOCAL_PREFIX.length));
  const plain = await aesGcmDecrypt(key, packed);
  return textDecoder.decode(plain);
};

const isSelfChat = ({ isFavorites, peerConnectId, localConnectId }) => {
  if (isFavorites || !peerConnectId) return true;
  if (localConnectId != null && String(peerConnectId) === String(localConnectId)) {
    return true;
  }
  return false;
};

/**
 * Encrypt outbound chat text.
 * - DM: X3DH + Double Ratchet
 * - favorites (self): local seal
 * - groups: plaintext (no shared-group ratchet yet)
 */
export const encryptOutgoingText = async ({
  chatId,
  peerConnectId,
  localConnectId = null,
  isGroup = false,
  isFavorites = false,
  plaintext,
}) => {
  if (isGroup) {
    return { body: plaintext, e2e_version: null, e2e_header: null };
  }
  if (isSelfChat({ isFavorites, peerConnectId, localConnectId })) {
    try {
      const body = await encryptLocalText(plaintext);
      const localHeader = { v: 1, type: 'local' };
      rememberOutbound(body, plaintext, null, localHeader);
      try { await persistOutboundCache(); } catch (_) { /* ignore */ }
      e2eLog('encrypt.local.ok', { chatId, header: localHeader });
      return {
        body,
        e2e_version: E2E_VERSION,
        e2e_header: localHeader,
      };
    } catch (err) {
      e2eLog('encrypt.fail', {
        chatId,
        err: err?.message || String(err),
        extra: { path: 'local' },
      });
      throw err;
    }
  }
  await ensureE2E();
  // Entire send path under chat lock — prevents parallel ensureAliceSession
  // from minting a new DH (n=0) for every rapid message.
  try {
    const { header, body } = await withChatLock(chatId, async () => {
      const state = await ensureAliceSession(chatId, peerConnectId);
      const out = await ratchetEncrypt(state, plaintext);
      await saveDrState(chatId, state);
      e2eLog('encrypt.ok', {
        chatId,
        header: out.header,
        state,
      });
      return out;
    });
    rememberOutbound(body, plaintext, null, header);
    // Flush sealed cache before WS echo / history reload can race.
    try {
      await Promise.all([persistOutboundCache(), persistPlainByMsgId()]);
    } catch (_) {
      // non-fatal — in-memory cache still holds plaintext
    }
    // Refill in background — never block send on prekey upload.
    replenishPrekeysIfNeeded().catch(() => {});
    return {
      body,
      e2e_version: E2E_VERSION,
      e2e_header: header,
    };
  } catch (err) {
    e2eLog('encrypt.fail', {
      chatId,
      err: err?.message || String(err),
      extra: { path: 'dm' },
    });
    throw err;
  }
};

/** @deprecated */
export const encryptDmText = encryptOutgoingText;

const packVoicePlain = (bytes, mime) => {
  const mimeBytes = textEncoder.encode((mime || 'audio/webm').slice(0, 64));
  const out = new Uint8Array(2 + mimeBytes.length + bytes.length);
  out[0] = 1;
  out[1] = mimeBytes.length;
  out.set(mimeBytes, 2);
  out.set(bytes, 2 + mimeBytes.length);
  return out;
};

const unpackVoicePlain = (plain) => {
  if (!plain || plain.length < 3) {
    return { bytes: plain || new Uint8Array(), mime: 'audio/webm' };
  }
  const mimeLen = plain[1];
  const mime = textDecoder.decode(plain.slice(2, 2 + mimeLen)) || 'audio/webm';
  return { bytes: plain.slice(2 + mimeLen), mime };
};

export const encryptOutgoingVoice = async ({
  chatId,
  peerConnectId,
  localConnectId = null,
  isGroup = false,
  isFavorites = false,
  blob,
}) => {
  const mime = ((blob.type || 'audio/webm').split(';')[0] || 'audio/webm').slice(0, 64);
  const bytes = new Uint8Array(await blob.arrayBuffer());

  if (isGroup) {
    return { blob, encrypted: false, e2e_version: null, e2e_header: null };
  }

  if (isSelfChat({ isFavorites, peerConnectId, localConnectId })) {
    await ensureLocalE2E();
    const identity = await loadIdentity();
    if (!identity?.secretKey) {
      throw new Error('e2e identity missing');
    }
    const key = kdf(identity.secretKey, 'LynxLocalSeal', 32);
    const packed = await aesGcmEncrypt(key, packVoicePlain(bytes, mime));
    const localHeader = { v: 1, type: 'local' };
    rememberOutboundVoice(localHeader, mime, bytes);
    return {
      blob: new Blob([packed], { type: 'application/x-lynx-e2e-audio' }),
      encrypted: true,
      e2e_version: E2E_VERSION,
      e2e_header: localHeader,
    };
  }
  await ensureE2E();

  const { header, body } = await withChatLock(chatId, async () => {
    const ready = await ensureAliceSession(chatId, peerConnectId);
    const out = await ratchetEncrypt(ready, packVoicePlain(bytes, mime));
    await saveDrState(chatId, ready);
    e2eLog('encrypt.voice.ok', { chatId, header: out.header, state: ready });
    return out;
  });
  rememberOutboundVoice(header, mime, bytes);
  replenishPrekeysIfNeeded().catch(() => {});
  const packed = decodeWireBody(body);
  return {
    blob: new Blob([packed], { type: 'application/x-lynx-e2e-audio' }),
    encrypted: true,
    e2e_version: E2E_VERSION,
    e2e_header: header,
  };
};

const voiceDecryptInflight = new Map();

export const decryptE2EVoiceBytes = async ({ chatId, e2eHeader, audioBytes, msgId = null }) => {
  await ensureLocalE2E();
  const header = typeof e2eHeader === 'string'
    ? (() => { try { return JSON.parse(e2eHeader); } catch (_) { return null; } })()
    : e2eHeader;
  const view = audioBytes instanceof Uint8Array ? audioBytes : new Uint8Array(audioBytes || []);
  const inflightKey = (msgId != null && String(msgId).toLowerCase())
    || headerCacheKey(header)
    || null;

  if (inflightKey && voiceDecryptInflight.has(inflightKey)) {
    return voiceDecryptInflight.get(inflightKey);
  }

  const run = (async () => {
  const cached = lookupOutboundVoice(header, msgId);
  if (cached?.audioB64) {
    try {
      e2eLog('decrypt.voice.cache', { chatId, header, msgId });
      return {
        bytes: b64ToBytes(cached.audioB64),
        mime: cached.mime || 'audio/webm',
      };
    } catch (_) {
      // fall through
    }
  }

  if (header?.type === 'local') {
    const identity = await loadIdentity();
    if (!identity?.secretKey) {
      throw new Error('e2e identity missing');
    }
    const key = kdf(identity.secretKey, 'LynxLocalSeal', 32);
    const plain = await aesGcmDecrypt(key, view);
    const unpacked = unpackVoicePlain(plain);
    rememberOutboundVoice(header, unpacked.mime, unpacked.bytes, msgId);
    return unpacked;
  }

  if (!header || !chatId) {
    throw new Error('missing e2e header');
  }

  try {
    await ensureE2E();
    const plain = await withChatLock(chatId, async () => {
      let state = await loadDrState(chatId);
      if ((!state || !state.established) && header.type === 'prekey') {
        state = await acceptPrekeyMessage(chatId, header);
      }
      if (!state || !state.established) {
        throw new Error('no session');
      }
      const p = await ratchetDecrypt(state, header, view);
      await saveDrState(chatId, state);
      return p;
    });
    const unpacked = unpackVoicePlain(plain);
    rememberOutboundVoice(header, unpacked.mime, unpacked.bytes, msgId);
    e2eLog('decrypt.voice.ok', { chatId, header, msgId });
    return unpacked;
  } catch (err) {
    const raced = lookupOutboundVoice(header, msgId);
    if (raced?.audioB64) {
      e2eLog('decrypt.voice.cache_race', { chatId, header, msgId });
      return {
        bytes: b64ToBytes(raced.audioB64),
        mime: raced.mime || 'audio/webm',
      };
    }
    e2eLog('decrypt.voice.fail', {
      chatId,
      header,
      err: err?.message || String(err),
      msgId,
    });
    throw err;
  }
  })();

  if (inflightKey) {
    voiceDecryptInflight.set(inflightKey, run);
    run.finally(() => {
      if (voiceDecryptInflight.get(inflightKey) === run) {
        voiceDecryptInflight.delete(inflightKey);
      }
    });
  }
  return run;
};

export const decryptDmMessage = async (message, {
  allowSessionReset = true,
  historySkipRatchet = false,
  localUserId = null,
} = {}) => {
  if (!message || typeof message !== 'object') {
    return message;
  }
  if (message.message_type === 'file_message' || message.message_type === 'voice_message') {
    return message;
  }
  if (typeof message.message !== 'string' || !message.message) {
    return message;
  }

  const body = message.message;
  const header = parseHeader(message.e2e_header);
  const msgId = message.id != null ? String(message.id).toLowerCase() : null;
  const hk = headerCacheKey(header);
  const isOwn = localUserId != null
    && message.sender_id != null
    && String(message.sender_id).toLowerCase() === String(localUserId).toLowerCase();

  const hitOwnCache = (plain, via) => {
    if (msgId) rememberPlain(msgId, plain);
    if (body) outboundPlainByBody.set(body, plain);
    if (hk) outboundPlainByHeader.set(hk, plain);
    e2eLog(via, { chatId: message.chat_id, msgId, header });
    return {
      ...message,
      message: plain,
      encrypted: true,
      e2e: true,
    };
  };

  const cannotDecrypt = () => {
    const own = resolveOwnPlaintext(message);
    if (own) {
      return hitOwnCache(own, 'decrypt.own_recover');
    }
    return {
      ...message,
      message: '🔒 cannot decrypt',
      encrypted: true,
      e2e: true,
    };
  };

  // Sync hit first — never await before checking in-memory outbound cache
  if (msgId && plainByMsgId.has(msgId)) {
    return hitOwnCache(plainByMsgId.get(msgId), 'decrypt.id_cache');
  }
  if (body && outboundPlainByBody.has(body)) {
    const plain = outboundPlainByBody.get(body);
    if (message.chat_id && header && !historySkipRatchet && !isOwn) {
      withChatLock(message.chat_id, async () => {
        const state = await loadDrState(message.chat_id);
        if (state?.established && replayOwnSend(state, header)) {
          await saveDrState(message.chat_id, state);
          e2eLog('decrypt.outbound_replay', {
            chatId: message.chat_id,
            msgId,
            header,
            state,
          });
        }
      }).catch(() => {});
    } else if (isOwn) {
      e2eLog('decrypt.outbound_cache', { chatId: message.chat_id, msgId, header });
    }
    return hitOwnCache(plain, 'decrypt.outbound_cache');
  }
  if (hk && outboundPlainByHeader.has(hk)) {
    return hitOwnCache(outboundPlainByHeader.get(hk), 'decrypt.outbound_header');
  }

  // Ensure identity exists so sealed outbound cache can be unlocked before lookups.
  try {
    if (!(await loadIdentity())) {
      await ensureLocalE2E();
    } else {
      await loadOutboundCache();
    }
  } catch (_) {
    await loadOutboundCache();
  }

  // Retry cache after disk unlock (refresh / late persist).
  if (msgId && plainByMsgId.has(msgId)) {
    return hitOwnCache(plainByMsgId.get(msgId), 'decrypt.id_cache');
  }
  if (body && outboundPlainByBody.has(body)) {
    return hitOwnCache(outboundPlainByBody.get(body), 'decrypt.outbound_cache');
  }
  if (hk && outboundPlainByHeader.has(hk)) {
    return hitOwnCache(outboundPlainByHeader.get(hk), 'decrypt.outbound_header');
  }

  // Own ciphertext can never be ratchet-decrypted after a DH step — only cache.
  if (isOwn) {
    try {
      await mergeOutboundCacheFromDisk();
    } catch (_) {
      // ignore
    }
    if (msgId && plainByMsgId.has(msgId)) {
      return hitOwnCache(plainByMsgId.get(msgId), 'decrypt.id_cache');
    }
    if (body && outboundPlainByBody.has(body)) {
      return hitOwnCache(outboundPlainByBody.get(body), 'decrypt.outbound_cache');
    }
    if (hk && outboundPlainByHeader.has(hk)) {
      return hitOwnCache(outboundPlainByHeader.get(hk), 'decrypt.outbound_header');
    }
    e2eLog('decrypt.own_miss', { chatId: message.chat_id, msgId, header });
    return cannotDecrypt();
  }

  if (historySkipRatchet) {
    const isE2E = message.e2e_version || (header && header.v === 1) || isE2EWireBody(body);
    if (isE2E || body.startsWith('lynx1.') || body.startsWith(LOCAL_PREFIX)) {
      return cannotDecrypt();
    }
    return message;
  }

  if (body.startsWith(LOCAL_PREFIX) || header?.type === 'local') {
    try {
      await ensureLocalE2E();
      const plain = await decryptLocalText(body.startsWith(LOCAL_PREFIX) ? body : body);
      return { ...message, message: plain, encrypted: true, e2e: true };
    } catch (_) {
      return cannotDecrypt();
    }
  }

  const isE2E = message.e2e_version || (header && header.v === 1) || isE2EWireBody(body);
  if (!isE2E) {
    if (body.startsWith('lynx1.')) {
      return cannotDecrypt();
    }
    return message;
  }

  try {
    await ensureLocalE2E();
  } catch (_) {
    return cannotDecrypt();
  }

  const packed = decodeWireBody(body);
  if (!packed || !header) {
    e2eLog('decrypt.bad_wire', {
      chatId: message.chat_id,
      msgId: message.id,
      header,
      extra: { hasPacked: Boolean(packed), hasHeader: Boolean(header) },
    });
    return cannotDecrypt();
  }

  const chatId = message.chat_id;
  if (!chatId) {
    e2eLog('decrypt.missing_chat_id', { msgId: message.id, header });
    return cannotDecrypt();
  }

  try {
    return await withChatLock(chatId, async () => {
      let state = await loadDrState(chatId);
      let freshPrekey = false;
      // Only bootstrap from prekey when we have no session. Re-accepting a
      // historical prekey regenerates random DHs and permanently breaks later msgs.
      if ((!state || !state.established) && header.type === 'prekey') {
        state = await acceptPrekeyMessage(chatId, header);
        freshPrekey = true;
        e2eLog('decrypt.accept_prekey', { chatId, msgId: message.id, header, state });
      }
      if (!state || !state.established) {
        throw new Error('no session');
      }
      const plain = await ratchetDecrypt(state, header, packed);
      const pendingOtp = state._pendingOtpKeyId;
      delete state._pendingOtpKeyId;
      await saveDrState(chatId, state);
      if (pendingOtp != null) {
        await removeOneTimePrekey(pendingOtp);
      }
      if (freshPrekey) {
        e2eAPI.markChatOpen(chatId).catch(() => {});
      }
      const text = textDecoder.decode(plain);
      rememberPlain(msgId, text);
      try {
        await persistPlainByMsgId();
      } catch (_) {
        // ignore
      }
      e2eLog('decrypt.ok', { chatId, msgId, header, state });
      return {
        ...message,
        message: text,
        encrypted: true,
        e2e: true,
      };
    });
  } catch (err) {
    e2eLog('decrypt.fail', {
      chatId,
      msgId,
      header,
      err: err?.message || String(err),
      state: await loadDrState(chatId).catch(() => null),
      extra: { allowSessionReset },
    });
    // Live only: peer started a fresh handshake. Never do this while replaying history.
    if (allowSessionReset && header.type === 'prekey' && chatId) {
      try {
        return await withChatLock(chatId, async () => {
          await deleteSession(chatId);
          const state = await acceptPrekeyMessage(chatId, header);
          const plain = await ratchetDecrypt(state, header, packed);
          const pendingOtp = state._pendingOtpKeyId;
          delete state._pendingOtpKeyId;
          await saveDrState(chatId, state);
          if (pendingOtp != null) {
            await removeOneTimePrekey(pendingOtp);
          }
          e2eAPI.markChatOpen(chatId).catch(() => {});
          const text = textDecoder.decode(plain);
          rememberPlain(msgId, text);
          e2eLog('decrypt.prekey_retry_ok', { chatId, msgId, header, state });
          return {
            ...message,
            message: text,
            encrypted: true,
            e2e: true,
          };
        });
      } catch (err2) {
        e2eLog('decrypt.prekey_retry_fail', {
          chatId,
          msgId,
          header,
          err: err2?.message || String(err2),
        });
        return cannotDecrypt();
      }
    }
    return cannotDecrypt();
  }
};

export const peerHasE2E = async (peerConnectId) => {
  if (!peerConnectId) return false;
  try {
    const { data } = await e2eAPI.getStatus(peerConnectId);
    return Boolean(data?.enabled);
  } catch (_) {
    return false;
  }
};

export const dropSession = async (chatId) => {
  await deleteSession(chatId);
};

export const peekChatHasSession = async (chatId) => {
  if (!chatId) return false;
  const state = await loadDrState(chatId);
  return Boolean(state?.established);
};

export const listDevices = async () => {
  const deviceId = await getLocalDeviceId();
  const { data } = await e2eAPI.listDevices(deviceId);
  return data?.devices || [];
};

export const revokeDevice = async (deviceId) => {
  const localId = await getLocalDeviceId();
  await e2eAPI.revokeDevice(deviceId);
  if (deviceId === localId) {
    await clearAllE2E();
    notifyE2EChanged();
  }
};

export const revokeOtherDevices = async () => {
  const localId = await getLocalDeviceId();
  if (!localId) {
    throw new Error('no local device');
  }
  const { data } = await e2eAPI.revokeOtherDevices(localId);
  return data;
};
